import { type PowerSyncDrizzleDB, user } from "@time-tracker/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this form:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * In other words, in guest mode the UPDATE went out **without `WHERE`** and
 * rewrote EVERY `user` row in the local database. `user` is not `localOnly`, so
 * every touched row also enqueued its own upload op in `ps_crud`.
 *
 * The premise behind it ("the guest database has only one `user` row") is false
 * when upgrading from guest to real: there the guest database CONNECTS to the
 * real account's connector and self-subscribes to synchronized streams, which
 * may download more `user` rows (organization co-members). The table then no
 * longer has only one row.
 *
 * The correct filter was not guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid` is exactly
 * `VISITOR_ID`. Thus `eq(user.id, userId)` targets the SAME row that the
 * unfiltered branch intended to touch when the table had one row—identical
 * behavior in the normal case and bounded otherwise. It is also the form already
 * used by `updateUserFontFamilyMutation` and `updateUserImageMutation`, neither
 * of which ever had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * With `patchRowVerified`, this `.where(...)` no longer depends on each mutation
 * writing it manually: `idColumn`/`id` are required function arguments, and the
 * helper itself builds the `WHERE`—there is no way to call it without a filter.
 * See `../powersync/verified-write.ts` §user for why `user` goes through here
 * like every other table.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through REST:
 * this makes the new photo appear immediately and survive a cold reload without
 * a connection. The BINARY does go through REST (`POST /api/avatar`) because it
 * does not fit in the sync queue; only the key returned by that upload travels
 * through here.
 *
 * `null` = "remove the photo" (initials return).
 *
 * A guest has no synchronizable row of their own and cannot have an avatar
 * (rule zero), so there is no `VISITOR_ID` branch here as there is for
 * `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}

/**
 * Shared Drizzle mutation for updating the presence status broadcast to the
 * team (`pulse-timer.tsx`, `idle-guard.tsx`).
 */
export async function updateUserPresenceMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  status: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { current_status: status }, options);
}
